import { DB_ORG } from "@/constants/db";
import { Avatar } from "@/layouts/signed-in/Avatar/Avatar";
import { IUser, UserAccessLevel } from "@/stores/user-store";
import { useUIModeStore } from "@/stores/ui-mode-store";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { CreditCardOutlined, SettingOutlined, RocketOutlined, ToolOutlined, TeamOutlined, AppstoreOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import type { FC } from "react";
import { BiLogOut } from "react-icons/bi";
import { GiBrain, GiTwoCoins } from "react-icons/gi";
import { MdHvac } from "react-icons/md";
import { TbArrowsExchange, TbMap2, TbScan, TbChecklist, TbReportAnalytics, TbEye } from "react-icons/tb";
import { NavLink, useNavigate } from "react-router-dom";
import { OrgDropdown } from "./OrgDropdown";

// Helper function to check if trial is expired
const isTrialExpired = (user: IUser | null | undefined): boolean => {
	if (!user?.is_trial && !user?.isTrial && !user?.isTrialAccount) {
		return false;
	}

	const trialExpirationDate =
		user?.trialExpirationDate || user?.trial_expiration_date;
	if (!trialExpirationDate) {
		// If no expiration date, check if account is disabled as fallback
		return user?.isAccountDisabled || false;
	}

	const expirationDate = new Date(trialExpirationDate);
	const now = new Date();
	return expirationDate <= now;
};

// Helper function to check if user has active access to the app
const hasActiveAccess = (user: IUser | null | undefined): boolean => {
	if (!user) return false;

	// Super admins always have access
	if (user?.role?.access_level >= UserAccessLevel.SUPER_ADMIN) {
		return true;
	}

	// Trial users have access if trial is not expired
	if (
		(user?.is_trial || user?.isTrial || user?.isTrialAccount) &&
		!isTrialExpired(user)
	) {
		return true;
	}

	// Non-trial users need an active subscription
	if (user?.hasActiveSubscription) {
		return true;
	}

	return false;
};

export interface SidebarProps {
	isSidebarOpen: boolean;
	user: IUser;
	updateUserOrg: (externalFirebaseId: string, fullName: string) => void;
	logout: () => Promise<void>;
}

// NavItem component for consistent styling and tooltip behavior
interface NavItemProps {
	to: string;
	icon: React.ReactNode;
	label: string;
	end?: boolean;
	onClick?: () => void;
}

const NavItem: FC<NavItemProps> = ({ to, icon, label, end, onClick }) => (
	<li className="sidebar-nav-item">
		<Tooltip
			title={label}
			placement="right"
			mouseEnterDelay={0.1}
			overlayClassName="sidebar-tooltip"
		>
			<NavLink
				to={to}
				end={end}
				className={({ isActive }) =>
					`sidebar-icon-link ${isActive ? "active" : ""}`
				}
				onClick={onClick}
			>
				<span className="sidebar-icon">{icon}</span>
			</NavLink>
		</Tooltip>
	</li>
);

export const Sidebar: FC<SidebarProps> = ({
	user,
	updateUserOrg,
	logout,
}) => {
	const { setMode } = useUIModeStore();
	const hasWorkOrders = useFeatureFlag("work_orders");
	const hasFacilityLink = useFeatureFlag("facility_link");
	const hasInventory = useFeatureFlag("inventory");
	
	// hasDataCollection removed — Data Collection is accessed via Vision Capture (/forms)
	const navigate = useNavigate();
	const canSwitchOrgs =
		!user.isTrialAccount &&
		!user.isTrial &&
		(user.allowedOrganizations?.length > 1 ||
			user.allowed_organizations?.length > 1);

	const handleSwitchToCommandCenter = () => {
		setMode("command-center");
		navigate("/");
	};

	return (
		<nav className="sidebar-slim">
			{/* Top Section - Avatar */}
			<div className="sidebar-top">
				<Tooltip
					title="My Profile"
					placement="right"
					mouseEnterDelay={0.1}
					overlayClassName="sidebar-tooltip"
				>
					<NavLink to="/profile" className="sidebar-avatar-link">
						<div className="sidebar-avatar">
							<Avatar alt="Avatar" className="avatar" />
						</div>
					</NavLink>
				</Tooltip>
			</div>

			{/* Main Navigation */}
			<ul className="sidebar-nav">
				{/* Command Center - Switch to AI-first mode */}
				{hasActiveAccess(user) && (
					<li className="sidebar-nav-item">
						<Tooltip
							title="Command Center"
							placement="right"
							mouseEnterDelay={0.1}
							overlayClassName="sidebar-tooltip"
						>
							<button
								type="button"
								className="sidebar-icon-link sidebar-command-center-btn"
								onClick={handleSwitchToCommandCenter}
							>
								<span className="sidebar-icon"><RocketOutlined /></span>
							</button>
						</Tooltip>
					</li>
				)}

				{/* Subscription - Visible for all users except super admins */}
				{user?.role?.access_level < UserAccessLevel.SUPER_ADMIN && (
					<NavItem
						to="/subscription"
						icon={<CreditCardOutlined />}
						label={
							(user?.is_trial || user?.isTrial) && !user?.isAccountDisabled
								? "Upgrade Plan"
								: "Subscription"
						}
					/>
				)}

				{/* Regular navigation - Show only for users with active access */}
				{hasActiveAccess(user) && (
					<>
						<NavItem
							to="/"
							icon={<GiBrain />}
							label="Dashboard"
							end
						/>
						{user?.organization?.external_firebase_id !==
							DB_ORG.CUSHMANWAKEFIELD &&
							(user?.hasPlusSubscription ||
								user?.is_trial ||
								user?.isTrial ||
								user?.isTrialAccount) && (
								<NavItem
									to="/asset-scanner"
									icon={<TbScan />}
									label="Asset Scanner"
								/>
							)}
						<NavItem
							to="/locations"
							icon={<TbMap2 />}
							label="Locations"
						/>
						<NavItem
							to="/assets"
							icon={<MdHvac />}
							label="Assets"
						/>
						<NavItem
							to="/forms"
							icon={<TbEye />}
							label="Vision Capture"
						/>
						{user?.organization?.external_firebase_id !==
							DB_ORG.CUSHMANWAKEFIELD &&
							(user?.hasPlusSubscription ||
								user?.is_trial ||
								user?.isTrial ||
								user?.isTrialAccount) && (
								<NavItem
									to="/quality-check"
									icon={<TbChecklist />}
									label="Quality Check"
								/>
							)}
						{hasWorkOrders && (
							<NavItem
								to="/work-orders"
								icon={<ToolOutlined />}
								label="Work Orders"
							/>
						)}
						{hasInventory && (
							<NavItem
								to="/inventory"
								icon={<AppstoreOutlined />}
								label="Inventory"
							/>
						)}
						{hasFacilityLink && (
						<NavItem
							to="/facility-link"
							icon={<TeamOutlined />}
							label="FacilityLink"
						/>
					)}
					
						{/* Data Collection is accessed via Vision Capture (/forms) */}
						{user?.organization?.external_firebase_id !==
							DB_ORG.CUSHMANWAKEFIELD &&
							(user?.hasPlusSubscription ||
								user?.is_trial ||
								user?.isTrial ||
								user?.isTrialAccount) && (
								<NavItem
									to="/predictive-spend"
									icon={<GiTwoCoins />}
									label="Predictive Spend"
								/>
							)}
						{(user?.role?.access_level ?? user?.role?.accessLevel ?? 0) >=
							UserAccessLevel.SUPER_ADMIN && (
							<NavItem
								to="/reports"
								icon={<TbReportAnalytics />}
								label="Reports"
							/>
						)}
					</>
				)}
			</ul>

			{/* Bottom Section - Admin, Org Switcher & Logout */}
			<div className="sidebar-bottom">
				{/* Administration - pinned to bottom as it's an admin function */}
				{hasActiveAccess(user) && (
					<NavItem
						to="/admin"
						icon={<SettingOutlined />}
						label="Administration"
					/>
				)}
				{/* Organization Switcher */}
				{canSwitchOrgs ? (
					<div className="sidebar-org-switcher">
						<OrgDropdown
							user={user}
							updateUserOrg={updateUserOrg}
							options={user.allowedOrganizations || user.allowed_organizations}
							compact
						/>
					</div>
				) : (
					<Tooltip
						title={user?.organization?.fullName || user?.organization?.full_name || "Organization"}
						placement="right"
						mouseEnterDelay={0.1}
						overlayClassName="sidebar-tooltip"
					>
						<div className="sidebar-org-icon">
							<TbArrowsExchange />
						</div>
					</Tooltip>
				)}

				{/* Logout */}
				<Tooltip
					title="Log Out"
					placement="right"
					mouseEnterDelay={0.1}
					overlayClassName="sidebar-tooltip"
				>
					<button
						type="button"
						className="sidebar-logout-btn"
						onClick={logout}
					>
						<BiLogOut />
					</button>
				</Tooltip>
			</div>
		</nav>
	);
};
